'use client';

/* ──────────────────────────────────────────────
 *  StepNavContext — état de navigation déclaré par
 *  l'étape courante, consommé par le shell.
 *  Le shell (StepFooter sticky en colonne unique,
 *  sidebar récap en layout wide) possède le rendu
 *  des actions ; les étapes ne rendent plus leurs
 *  boutons back/CTA inline.
 * ────────────────────────────────────────────── */

import { type JSX, type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';

export interface StepNavPrimary {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Affiche un spinner à la place du label (soumission en cours). */
  loading?: boolean;
}

export interface StepNav {
  /** Retour à l'étape précédente (absent sur la première étape). */
  onBack?: () => void;
  /** Action principale (absente pour les étapes à auto-avance : lieu, adresse). */
  primary?: StepNavPrimary;
  /** L'étape rend son propre bouton principal (ex. paiement Stripe) : le shell n'affiche que back + total. */
  ownsPrimary?: boolean;
}

interface StepNavContextValue {
  stepNav: StepNav | null;
  setStepNav: (nav: StepNav | null) => void;
}

const StepNavCtx = createContext<StepNavContextValue | null>(null);

export function StepNavProvider({ children }: { children: ReactNode }): JSX.Element {
  const [stepNav, setStepNav] = useState<StepNav | null>(null);
  const value = useMemo((): StepNavContextValue => ({ stepNav, setStepNav }), [stepNav]);
  return <StepNavCtx.Provider value={value}>{children}</StepNavCtx.Provider>;
}

/** Navigation de l'étape courante (null hors étapes : loading / error / success). */
export function useStepNav(): StepNav | null {
  const ctx = useContext(StepNavCtx);
  return ctx?.stepNav ?? null;
}

/**
 * Déclare la navigation de l'étape courante. À appeler avec un objet mémoïsé
 * (`useMemo`) pour éviter les re-registrations à chaque render.
 */
export function useRegisterStepNav(nav: StepNav): void {
  const ctx = useContext(StepNavCtx);
  if (!ctx) throw new Error('useRegisterStepNav must be used within StepNavProvider');
  const { setStepNav } = ctx;
  useEffect((): (() => void) => {
    setStepNav(nav);
    return (): void => setStepNav(null);
  }, [nav, setStepNav]);
}
